import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';

/**
 * Button — primary / secondary / ghost / danger variants × sm/md/lg sizes.
 *
 * Accessibility:
 *   - Native <button> element; keyboard + screen-reader friendly out of the box
 *   - Disabled state visible (opacity) + non-interactive (cursor: not-allowed)
 *   - focus-visible ring inherited from global.css (Hunter outline)
 *
 * Phase 11 Block 6 PR 2.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

const variantStyles: Record<ButtonVariant, React.CSSProperties> = {
  primary: {
    background: 'var(--brand-primary)',
    color: 'var(--brand-on-primary)',
    border: '1px solid var(--brand-primary)',
  },
  secondary: {
    background: 'var(--color-card)',
    color: 'var(--color-ink)',
    border: '1px solid var(--color-border)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--brand-primary)',
    border: '1px solid transparent',
  },
  danger: {
    background: 'var(--color-status-red)',
    color: '#fff',
    border: '1px solid var(--color-status-red)',
  },
};

// `minHeight` gives each size a comfortable desktop hit area without changing the
// visual padding/look (M3 touch-target discipline); the coarse-pointer floor in
// global.css lifts every button to ≥48px on touch/mobile.
const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
  sm: {
    fontSize: 'var(--fs-sm)',
    padding: '6px 10px',
    borderRadius: 'var(--r-md)',
    gap: 'var(--sp-1-5)',
    minHeight: 32,
  },
  md: {
    fontSize: 'var(--fs-md)',
    padding: '8px 14px',
    borderRadius: 'var(--r-md)',
    gap: 'var(--sp-2)',
    minHeight: 40,
  },
  lg: {
    fontSize: 'var(--fs-lg)',
    padding: '10px 18px',
    borderRadius: 'var(--r-lg)',
    gap: 'var(--sp-2)',
    minHeight: 44,
  },
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, disabled, iconLeft, iconRight, children, style, ...rest },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      style={{
        ...variantStyles[variant],
        ...sizeStyles[size],
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'inherit',
        fontWeight: 'var(--fw-medium)',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.6 : 1,
        transition: 'background 120ms ease, border-color 120ms ease, transform 80ms ease',
        ...style,
      }}
      {...rest}
    >
      {loading && <Spinner size="sm" />}
      {!loading && iconLeft}
      {children}
      {iconRight}
    </button>
  );
});

// Inline Spinner for the loading state — keeps Button self-contained.
// Real Spinner export lives in its own atom (web/src/atoms/Spinner.tsx).
function Spinner({ size }: { size: 'sm' | 'md' }) {
  const px = size === 'sm' ? 12 : 16;
  return (
    <span
      role="status"
      aria-label="Loading"
      style={{
        display: 'inline-block',
        width: px,
        height: px,
        border: '2px solid currentColor',
        borderTopColor: 'transparent',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }}
    />
  );
}
